import { LOW_BATTERY_PCT } from './constants';

/**
 * v22 BOLT-116 — what each player's device is doing, in one place.
 *
 * Connection and GPS were already on the wire (v19 BOLT-087) but only fed the
 * host's start-gate; battery is the one addition. Both players should be able
 * to see whether their partner is connected, on precise GPS, and about to run
 * out of phone, in the lobby AND during the round.
 *
 * PRIVACY: booleans, a radius, and a coarse percentage. Never a coordinate.
 */
export interface StatusSlot {
  name: string;
  connected: boolean;
  locationPrecise?: boolean;
  hasFix?: boolean;
  accuracyM?: number;
  batteryPct?: number;
}

export type ConnState = 'connected' | 'reconnecting';
export type GpsState = 'precise' | 'waiting' | 'nofix';
export type BatteryState = 'ok' | 'low' | 'unknown';

export function connState(slot: StatusSlot): ConnState {
  return slot.connected ? 'connected' : 'reconnecting';
}

export function gpsState(slot: StatusSlot): GpsState {
  if (!slot.hasFix) return 'nofix';
  return slot.locationPrecise ? 'precise' : 'waiting';
}

/**
 * `unknown` is a first-class answer, not a failure. The Web Battery Status API
 * is unsupported on Safari and every iOS browser (and Firefox); only Chromium
 * has it, gated. So a web player reports nothing and we say so, rather than
 * over-promising a universal battery readout.
 */
export function batteryState(slot: StatusSlot): BatteryState {
  if (slot.batteryPct == null) return 'unknown';
  return slot.batteryPct <= LOW_BATTERY_PCT ? 'low' : 'ok';
}

/** The battery glyph text. Em dash is banned, so unknown reads as a hyphen. */
export function batteryLabel(slot: StatusSlot): string {
  return slot.batteryPct == null ? '-' : `${Math.round(slot.batteryPct)}%`;
}

/**
 * The host's start caution. A WARNING, never a wall: it mirrors the readiness
 * soft-gate posture, because a dying phone is the players' call, not the app's.
 * Only players who actually report battery can trigger it.
 */
export function lowBatteryWarning(slots: (StatusSlot | null | undefined)[]): string | null {
  const low = slots
    .filter((s): s is StatusSlot => !!s && s.batteryPct != null && s.batteryPct <= LOW_BATTERY_PCT)
    // v28 BOLT-154 (Jake): "change to 'Warning: Orange team is at 20%
    // battery.'". The old sentence spent its second half on a general fact
    // ("GPS play drains fast") that the player can do nothing with, and its
    // first half never said what the number WAS. Now the number is labelled and
    // the whole line is one flagged fact. Name and percentage interpolated -
    // the name comes from `teamLabel`, so this reads "Orange Team" verbatim.
    .map((s) => `${s.name} is at ${Math.round(s.batteryPct!)}% battery`);
  if (!low.length) return null;
  return `Warning: ${low.join(', ')}.`;
}

/**
 * v24 FIELD BUG (Jake, 2026-07-29): "the status dropdown … lists both players
 * twice".
 *
 * `state.players` exposes the same two people under FOUR keys: in a series
 * every player is both a role (hider/seeker) and a team (A/B), so the naive
 * `[hider, seeker, teamA, teamB]` list showed each of them twice. Dedupe by
 * identity, keeping the first (role) occurrence's order.
 */
export function dedupeSlots<T extends StatusSlot>(slots: (T | null | undefined)[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const s of slots) {
    if (!s) continue;
    const key = s.name.trim().toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

/**
 * The GPS readout. Jake: "neutral coloured icons and then a status indicator or
 * value next to each" — accuracy IS the meaningful number for GPS, the same way
 * a percentage is for battery, so show it instead of a colour nobody can decode.
 */
export function gpsLabel(slot: StatusSlot): string {
  // v28 BOLT-154 (Jake): "for gps, 'no fix' should instead say 'none'." The row
  // reads "GPS: <value>", so "no fix" restated the label; "none" answers it.
  if (!slot.hasFix) return 'none';
  if (slot.accuracyM == null) return slot.locationPrecise ? 'precise' : 'coarse';
  const ft = Math.round(slot.accuracyM * 3.28084);
  return `${ft} ft`;
}

/** Connection has no meaningful number, so it keeps the colour-coded dot. */
export function connLabel(slot: StatusSlot): string {
  return slot.connected ? 'online' : 'reconnecting';
}
